import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  handleErrorToast,
  handleSuccessToast,
} from 'src/components/molecules/Toast/Toast';
import {
  EditCaseAccessMutationVariables,
  RemoveFromCaseMutationVariables,
  useEditCaseAccessMutation,
  useRemoveFromCaseMutation,
} from 'src/graphql';
import { QueryClient } from 'react-query';

type RemoveCaseTeamMemberPayload = {
  mutationRemoveCaseTeamMember: ReturnType<
    typeof useRemoveFromCaseMutation
  >['mutateAsync'];
  queryClient: QueryClient;
} & RemoveFromCaseMutationVariables;

export const removeCaseTeamMember = createAsyncThunk(
  'case/removeCaseTeamMember',
  async (
    {
      mutationRemoveCaseTeamMember,
      userId,
      caseId,
      queryClient,
    }: RemoveCaseTeamMemberPayload,
    _thunkAPI,
  ) => {
    const data = await mutationRemoveCaseTeamMember(
      {
        userId,
        caseId,
      },
      {
        onSuccess: async () => {
          handleSuccessToast({
            toastMessage: 'Team member removed',
            toastDescription: 'You have successfully updated your team',
          });
        },
        onError: () => {
          handleErrorToast({
            toastMessage: 'Error',
            toastDescription: 'There was a problem removing the team member',
          });
        },
      },
    );

    await queryClient.invalidateQueries(['CaseById']);

    return data;
  },
);

type EditCaseAccessPayload = {
  mutationEditCaseAccess: ReturnType<
    typeof useEditCaseAccessMutation
  >['mutateAsync'];
  queryClient: QueryClient;
} & EditCaseAccessMutationVariables;

export const editCaseTeamMemberAccess = createAsyncThunk(
  'case/editCaseTeamMemberAccess',
  async (
    {
      mutationEditCaseAccess,
      userId,
      caseId,
      entityPermissions,
      queryClient,
    }: EditCaseAccessPayload,
    _thunkAPI,
  ) => {
    const data = await mutationEditCaseAccess(
      {
        userId,
        caseId,
        entityPermissions,
      },
      {
        onSuccess: async () => {
          handleSuccessToast({
            toastMessage: 'Access level updated',
            toastDescription:
              "You have successfully adjusted a team member's permission level",
          });
        },
        onError: () => {
          handleErrorToast({
            toastMessage: 'Error',
            toastDescription: 'There was a problem updating case access',
          });
        },
      },
    );

    await queryClient.invalidateQueries(['CaseById']);

    return data;
  },
);
